import React, { useEffect, useMemo, useState } from "react";
import { FiDownload } from "react-icons/fi";
import DataTable from "./DataTable";
import StatusPill from "./StatusPill";
import { divergencias, resumoTipos } from "../mockData";
import validacaoFiscalService from "../../../services/validacaoFiscalService";

const GRAVIDADE_VARIANT = { alta: "danger", media: "warning", baixa: "neutral" };
const GRAVIDADE_LABEL = { alta: "Alta", media: "Média", baixa: "Baixa" };

function formatCurrency(num) {
  if (num === null || num === undefined) return "\u2014";
  return `R$ ${Number(num).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`;
}

export default function DivergenciasTab({ competenciaId, onAbrirDetalhe }) {
  const [tipoFiltro, setTipoFiltro] = useState("todos");
  const [busca, setBusca] = useState("");
  const [realLinhas, setRealLinhas] = useState(null);
  const [resumoReal, setResumoReal] = useState(null);

  useEffect(() => {
    if (!competenciaId) {
      setRealLinhas(null);
      setResumoReal(null);
      return;
    }
    let cancelado = false;
    const params = {};
    if (tipoFiltro !== "todos") params.tipo = tipoFiltro;
    if (busca) params.busca = busca;

    validacaoFiscalService
      .listarDivergencias(competenciaId, params)
      .then((resp) => {
        if (cancelado) return;
        setRealLinhas(resp?.data?.data || []);
        setResumoReal(resp?.data?.resumoPorTipo || []);
      })
      .catch((err) => console.error("[DivergenciasTab] Erro ao listar divergências:", err));

    return () => {
      cancelado = true;
    };
  }, [competenciaId, tipoFiltro, busca]);

  const linhasMock = useMemo(() => {
    return divergencias.filter((d) => {
      if (tipoFiltro !== "todos" && d.tipo !== tipoFiltro) return false;
      if (busca) {
        const termo = busca.toLowerCase();
        const alvo = `${d.chave} ${d.causa} ${d.responsavel}`.toLowerCase();
        if (!alvo.includes(termo)) return false;
      }
      return true;
    });
  }, [tipoFiltro, busca]);

  const usandoReal = !!competenciaId;
  const linhas = usandoReal
    ? (realLinhas || []).map((d) => ({
        id: d.id,
        tipo: d.codigo,
        chave: d.chaveAcesso || d.documentoId || "—",
        valor: formatCurrency(d.valor),
        causa: d.causa,
        baseLegal: (d.baseLegal || []).join("; "),
        acaoSugerida: d.acaoSugerida,
        responsavel: d.responsavelId || "Não atribuído",
        gravidade: d.gravidade,
      }))
    : linhasMock;
  const chips = usandoReal ? resumoReal || [] : resumoTipos;

  const columns = [
    { key: "tipo", label: "Tipo", render: (row) => <strong>{row.tipo}</strong> },
    {
      key: "chave",
      label: "Documento (chave)",
      render: (row) => (
        <button type="button" className="link-chave" onClick={() => onAbrirDetalhe && onAbrirDetalhe(row.id)}>
          {row.chave}
        </button>
      ),
    },
    { key: "valor", label: "Valor CBS" },
    { key: "causa", label: "Causa provável" },
    { key: "baseLegal", label: "Base legal" },
    { key: "acaoSugerida", label: "Ação sugerida" },
    { key: "responsavel", label: "Responsável" },
    {
      key: "gravidade",
      label: "Gravidade",
      render: (row) => (
        <StatusPill variant={GRAVIDADE_VARIANT[row.gravidade]}>{GRAVIDADE_LABEL[row.gravidade]}</StatusPill>
      ),
    },
  ];

  return (
    <>
      <div className="header-info" style={{ paddingTop: 0 }}>
        <div className="header-details">
          <h3 style={{ fontSize: "1.3em" }}>Fila de divergências</h3>
          <div className="header-meta">
            <span className="update-tag">{usandoReal ? "Dados reais da competência ativa" : "Competência 09/2026 · CBS (exemplo)"}</span>
          </div>
        </div>
        <div className="header-actions">
          <button type="button" className="icon-btn" disabled title="Disponível em breve">
            <FiDownload /> Exportar fila (CSV)
          </button>
        </div>
      </div>

      <div className="filter-bar">
        <label className="filter-field">
          Empresa
          <select disabled>
            <option>Todas as raízes</option>
          </select>
        </label>
        <label className="filter-field">
          Tipo
          <select value={tipoFiltro} onChange={(e) => setTipoFiltro(e.target.value)}>
            <option value="todos">D01–D12 (todos)</option>
            {resumoTipos.map((t) => (
              <option key={t.tipo} value={t.tipo}>
                {t.tipo} — {t.descricao}
              </option>
            ))}
          </select>
        </label>
        <label className="filter-field">
          Situação
          <select disabled>
            <option>Abertas</option>
          </select>
        </label>
        <label className="filter-field grow">
          Buscar por chave, fornecedor ou documento
          <input
            type="text"
            placeholder="44 dígitos ou CNPJ"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
          />
        </label>
      </div>

      <div className="type-chips">
        {chips.map((t) => (
          <div
            key={t.tipo || t.codigo}
            className={`type-chip ${tipoFiltro === (t.tipo || t.codigo) ? "active" : ""}`}
            onClick={() => {
              const val = t.tipo || t.codigo;
              setTipoFiltro(tipoFiltro === val ? "todos" : val);
            }}
          >
            <strong>{t.tipo || t.codigo}</strong> {t.descricao} · {t.qtde}
          </div>
        ))}
      </div>

      <DataTable columns={columns} rows={linhas} minWidth={1080} />

      <div className="table-footnote">
        {usandoReal
          ? "Divergências geradas pelo motor de conciliação (regras D01–D12)."
          : "Dados ilustrativos. Gravidade e tolerâncias são parâmetros por cliente."}
      </div>
    </>
  );
}
